import { type KeyValueStorageImplementation } from './KeyValueStorage'

const DATABASE_NAME = 'key-value-storage'
const DATABASE_VERSION = 1
const STORE_NAME = 'entries'

let databaseInstance: IDBDatabase | null = null
let databasePromise: Promise<IDBDatabase> | null = null

const getDatabase = async (): Promise<IDBDatabase> => {
    if (databaseInstance) {
        return databaseInstance
    }

    if (databasePromise) {
        return databasePromise
    }

    databasePromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION)

        request.onupgradeneeded = () => {
            const database = request.result

            if (!database.objectStoreNames.contains(STORE_NAME)) {
                database.createObjectStore(STORE_NAME)
            }
        }

        request.onsuccess = () => {
            databaseInstance = request.result
            databaseInstance.onclose = () => {
                databaseInstance = null
                databasePromise = null
            }

            resolve(databaseInstance)
        }

        request.onerror = () => {
            databasePromise = null
            reject(request.error)
        }
    })

    return databasePromise
}

const withStore = async <TValue>({
    action,
    mode,
}: {
    action: (store: IDBObjectStore) => IDBRequest<TValue>
    mode: IDBTransactionMode
}) => {
    const database = await getDatabase()

    return new Promise<TValue>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, mode)
        const store = transaction.objectStore(STORE_NAME)
        const request = action(store)

        request.onsuccess = () => {
            resolve(request.result)
        }

        request.onerror = () => {
            reject(request.error)
        }

        transaction.onerror = () => {
            reject(transaction.error)
        }
    })
}

const listKeys = async () => {
    if (typeof IDBObjectStore !== 'undefined' && 'getAllKeys' in IDBObjectStore.prototype) {
        const keys = await withStore({
            action: (store) => store.getAllKeys(),
            mode: 'readonly',
        })

        return keys.map((key) => `${key}`)
    }

    const database = await getDatabase()

    return new Promise<string[]>((resolve, reject) => {
        const transaction = database.transaction(STORE_NAME, 'readonly')
        const store = transaction.objectStore(STORE_NAME)
        const request = store.openKeyCursor()
        const keys: string[] = []

        request.onsuccess = () => {
            const cursor = request.result

            if (!cursor) {
                resolve(keys)
                return
            }

            keys.push(`${cursor.primaryKey}`)
            cursor.continue()
        }

        request.onerror = () => {
            reject(request.error)
        }

        transaction.onerror = () => {
            reject(transaction.error)
        }
    })
}

export const indexedDbKeyValueStorageImplementation: KeyValueStorageImplementation = {
    get: async (key) => {
        const value = await withStore({
            action: (store) => store.get(key),
            mode: 'readonly',
        })

        return typeof value === 'string' ? value : null
    },

    set: async (key, value) => {
        await withStore({
            action: (store) => store.put(value, key),
            mode: 'readwrite',
        })
    },

    remove: async (key) => {
        await withStore({
            action: (store) => store.delete(key),
            mode: 'readwrite',
        })
    },

    clear: async () => {
        await withStore({
            action: (store) => store.clear(),
            mode: 'readwrite',
        })
    },

    listKeys,
}
